// frontend/src/components/LooksGallery.js
// Shared Looks display component used on:
//   - ShopLanding (shop public page)
//   - ProfessionalProfile (staff public profile)
//   - FreelancerProfile (R4/R7 public profile, future)
//
// Props:
//   looks           — array of look objects from /api/looks/public
//   sectionTitle    — string, defaults to "Recent Looks"
//   isMobile        — boolean
//   onBookService   — fn(look) called when "Book This Look" is clicked.
//                     Parent supplies navigation logic.
//   viewAllPath     — reserved for future feed page integration
//   navigate        — reserved for future feed page integration

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaChevronLeft, FaChevronRight, FaRegComment, FaRegHeart, FaClock, FaTags } from "react-icons/fa";
import ShareButtons from "./ShareButtons";
import LikesButton from "./LikesButton";
import CommentsSection from "./CommentsSection";
import SaveButton from "./looks/SaveButton";
import RepostButton from "./looks/RepostButton";
import "./LooksGallery.css";
import { resolveLookShareUrl, resolveLookShareImageUrl } from "../utils/lookShareUtils";

function getLookImages(look) {
  if (Array.isArray(look?.images) && look.images.length > 0) {
    return look.images;
  }
  if (look?.image?.url) {
    return [{ url: look.image.url }];
  }
  return [];
}

function getOrCreateLookViewerId() {
  const storageKey = "ogestyle_look_viewer_id";
  let viewerId = localStorage.getItem(storageKey);

  if (!viewerId) {
    const randomPart =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    viewerId = `lv-${randomPart}`;
    localStorage.setItem(storageKey, viewerId);
  }

  return viewerId;
}

function LooksGallery({ looks = [], sectionTitle = "Recent Looks", isMobile = false, onBookService, viewAllPath, navigate }) {
  const navigateHook = useNavigate();
  const nav = navigate || navigateHook;
  const apiBase = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  const commentsRef = useRef(null);
  const touchStartX = useRef(null);
  const [modal, setModal] = useState({
    isOpen: false,
    lookIndex: 0,
    imageIndex: 0,
    look: null,
    loading: false,
    error: "",
  });
  const [visibleCount, setVisibleCount] = useState(isMobile ? 6 : 18);

  useEffect(() => {
    setVisibleCount(isMobile ? 6 : 18);
  }, [isMobile, looks.length]);

  const closeLightbox = () =>
    setModal({
      isOpen: false,
      lookIndex: 0,
      imageIndex: 0,
      look: null,
      loading: false,
      error: "",
    });

  const recordLookView = (lookId) => {
    if (!lookId) return;
    const storageKey = `look_viewed_${lookId}`;
    if (localStorage.getItem(storageKey)) return;
    const headers = { "x-viewer-id": getOrCreateLookViewerId() };
    const token = localStorage.getItem("ogestyle_token");
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${apiBase}/api/looks/${lookId}/view`, { method: "POST", headers })
      .then(() => localStorage.setItem(storageKey, "1"))
      .catch(() => {});
  };

  const openLightbox = (lookIndex) => {
    const feedLook = looks[lookIndex];
    setModal({
      isOpen: true,
      lookIndex,
      imageIndex: 0,
      look: null,
      loading: true,
      error: "",
    });
    if (feedLook?._id) recordLookView(feedLook._id);
  };

  const openLookInLightbox = (lookId) => {
    const idx = looks.findIndex((l) => String(l._id) === String(lookId));
    if (idx >= 0) {
      setModal((m) => ({
        ...m,
        lookIndex: idx,
        imageIndex: 0,
        look: null,
        loading: true,
        error: "",
      }));
      recordLookView(lookId);
    } else if (typeof nav === "function") {
      nav(`/look/${lookId}`);
    }
  };

  // Keyboard navigation for the lightbox
  useEffect(() => {
    if (!modal.isOpen) return;

    const activeLook = modal.look || looks[modal.lookIndex];
    const images = getLookImages(activeLook);

    const handleKey = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft" && modal.imageIndex > 0) {
        setModal((prev) => ({ ...prev, imageIndex: prev.imageIndex - 1 }));
      } else if (e.key === "ArrowRight" && modal.imageIndex < images.length - 1) {
        setModal((prev) => ({ ...prev, imageIndex: prev.imageIndex + 1 }));
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modal, looks]);

  // Load the enriched single-look payload whenever the lightbox opens or
  // switches look — gives the lightbox the same data as the Look page
  // (staff name, business, live price/duration, likes, more looks).
  useEffect(() => {
    if (!modal.isOpen) return;
    const feedLook = looks[modal.lookIndex];
    if (!feedLook?._id) return;
    let cancelled = false;
    setModal((m) => ({ ...m, look: null, loading: true, error: "" }));
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/looks/${feedLook._id}/public`);
        const data = await res.json();
        if (!res.ok || !data?.look) {
          throw new Error(data?.message || "Look not found");
        }
        if (!cancelled) {
          setModal((m) => ({ ...m, look: data.look, loading: false }));
        }
      } catch (err) {
        if (!cancelled) {
          setModal((m) => ({ ...m, look: null, loading: false, error: err.message }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modal.isOpen, modal.lookIndex, apiBase, looks]);

  if (!looks || looks.length === 0) return null;

  const displayedLooks = looks.slice(0, visibleCount);
  const hasMore = visibleCount < looks.length;

  const activeLook = modal.look || looks[modal.lookIndex];
  const activeImages = modal.isOpen ? getLookImages(activeLook) : [];
  const activeImage = activeImages[modal.imageIndex];

  const serviceName = activeLook?.bookingSnapshot?.serviceName || "Beauty Look";
  const proName = activeLook?.staffName || "Professional";
  const bizName = activeLook?.businessName || "";
  const roleLabel =
    activeLook?.staffRole === "R4"
      ? "Freelancer"
      : activeLook?.staffRole === "R7"
        ? "Booth Renter"
        : "Professional";
  const proImage = activeLook?.staffProfileImage || null;
  const initials =
    proName
      .split(" ")
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "OG";

  const duration =
    activeLook?.durationMinutes ||
    activeLook?.bookingSnapshot?.durationMinutes ||
    null;
  const price =
    activeLook?.price ?? activeLook?.bookingSnapshot?.servicePrice ?? null;
  const priceType = activeLook?.priceType || "from";
  const maxPrice = activeLook?.maxPrice || null;
  const priceLabel = (() => {
    if (!price) return null;
    if (priceType === "variable") return "Price varies";
    if (priceType === "hourly") return `$${Number(price).toFixed(2)}/hr`;
    if (priceType === "range" && maxPrice)
      return `$${Number(price).toFixed(2)} – $${Number(maxPrice).toFixed(2)}`;
    if (priceType === "fixed") return `$${Number(price).toFixed(2)}`;
    return `$${Number(price).toFixed(2)}`;
  })();

  const shareUrl = activeLook?._id
    ? resolveLookShareUrl(activeLook._id, activeLook.cardVersion)
    : "";

  const handleBook = () => {
    if (typeof onBookService === "function" && activeLook) {
      onBookService(activeLook);
    }
  };

  // Tap-to-advance + swipe navigation for the lightbox image.
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx =
      (e.changedTouches?.[0]?.clientX ?? touchStartX.current) -
      touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) {
      if (dx < 0) {
        setModal((prev) => ({
          ...prev,
          imageIndex: (prev.imageIndex + 1) % activeImages.length,
        }));
      } else {
        setModal((prev) => ({
          ...prev,
          imageIndex:
            (prev.imageIndex - 1 + activeImages.length) % activeImages.length,
        }));
      }
    }
  };
  const handleImageTap = (e) => {
    if (activeImages.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 2) {
      setModal((prev) => ({
        ...prev,
        imageIndex:
          (prev.imageIndex - 1 + activeImages.length) % activeImages.length,
      }));
    } else {
      setModal((prev) => ({
        ...prev,
        imageIndex: (prev.imageIndex + 1) % activeImages.length,
      }));
    }
  };

  return (
    <>
      {/* Gallery Section */}
      <div
        style={{
          padding: isMobile ? "0 16px 16px" : "8px 48px 20px",
          background: "transparent",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Header row */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            marginBottom: isMobile ? "8px" : "10px",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontWeight: "700",
              color: "#111827",
              fontSize: isMobile ? "13px" : "15px",
              lineHeight: "1.2",
            }}
          >
            {sectionTitle}
          </h2>
        </div>

        {/* Grid — fluid by column width (see LooksGallery.css): six fixed columns on an iPad left each
            look thumbnail ~150 px. */}
        <div className="looks-gallery-grid">
          {displayedLooks.map((look, lookIndex) => {
            const images = getLookImages(look);
            const coverImage = images[0]?.url;
            const serviceName = look?.bookingSnapshot?.serviceName || "Service";
            const multiCount = images.length > 1 ? images.length : null;

            return (
              <div
                key={look._id}
                style={{
                  background: "transparent",
                  border: "none",
                  borderRadius: "0",
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                {/* Image area */}
                <div
                  onClick={() => openLightbox(lookIndex)}
                  style={{ cursor: "pointer", background: "#111", lineHeight: 0, position: "relative" }}
                >
                  {coverImage && (
                    <img
                      src={coverImage}
                      alt={serviceName}
                      style={{
                        width: "100%",
                        // The upload crop is 4:5, but at a service-card width a natural 4:5 image made
                        // the card ~380px tall — far taller than the service cards beside it. A capped
                        // box with `cover` keeps the card in proportion and trims only the edges.
                        height: isMobile ? "150px" : "200px",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "0",
                      }}
                    />
                  )}
                  {/* Multi-image indicator */}
                  {multiCount && (
                    <span
                      style={{
                        position: "absolute",
                        top: "7px",
                        right: "7px",
                        background: "rgba(0,0,0,0.82)",
                        color: "#fff",
                        borderRadius: "999px",
                        border: "1px solid rgba(255,255,255,0.85)",
                        padding: "2px 7px",
                        fontSize: "11px",
                        fontWeight: "700",
                        lineHeight: "1",
                      }}
                    >
                      1/{multiCount}
                    </span>
                  )}
                </div>

                {/* Engagement counts — visible to all (social proof) */}
                <div style={{ padding: "4px 2px 0", textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#9ca3af",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                      <FaRegHeart size={10} /> {(look?.likeCount || 0).toLocaleString()}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                      <FaRegComment size={10} /> {(look?.commentCount || 0).toLocaleString()}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "3px" }}>
                      <FaEye size={10} /> {(look?.viewCount || 0).toLocaleString()}
                    </span>
                  </div>
                </div>

                {onBookService && (
                  <div style={{ padding: "6px 2px 2px", textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "5px",
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {serviceName}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onBookService(look);
                      }}
                      style={{
                        // Sized to match the service card's Book pill (service-tile-cta): a small inline
                        // chip, not a full-width banner, so a Looks card and a service card read the same.
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: 0,
                        border: "none",
                        borderRadius: "6px",
                        // Same action colour as the service tile's Book pill: both read
                        // --color-primary so the two card types cannot drift apart.
                        background: "var(--color-primary, #2d6da3)",
                        color: "#fff",
                        padding: "4px 9px",
                        fontSize: "0.6875rem",
                        fontWeight: "600",
                        lineHeight: 1.4,
                        cursor: "pointer",
                        // One line, always: the label wrapped to "Book This / Look" in a narrow card.
                        whiteSpace: "nowrap",
                        width: "auto",
                        textAlign: "center",
                      }}
                    >
                      Book This Look
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hasMore && (
          <div style={{ marginTop: "12px", display: "flex", justifyContent: "center" }}>
            <button
              onClick={() => setVisibleCount((prev) => prev + (isMobile ? 6 : 18))}
              style={{
                border: "1px solid #d1d5db",
                background: "#fff",
                color: "#374151",
                borderRadius: "0",
                padding: isMobile ? "7px 11px" : "8px 14px",
                fontSize: isMobile ? "11px" : "12px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {modal.isOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,6,23,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: "14px",
          }}
          onClick={closeLightbox}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1040px",
              maxHeight: "94vh",
              background: "#fff",
              borderRadius: "18px",
              overflowY: "auto",
              overflowX: "hidden",
              boxShadow: "0 24px 60px rgba(2,6,23,0.55)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              aria-label="Close"
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 20,
                width: "36px",
                height: "36px",
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(15,23,42,0.85)",
                color: "#fff",
                fontSize: "20px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(2,6,23,0.35)",
              }}
            >
              ×
            </button>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
                minHeight: 0,
              }}
            >
              {/* Image side */}
              <div
                onClick={handleImageTap}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                style={{
                  position: "relative",
                  background: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "260px",
                  cursor: activeImages.length > 1 ? "pointer" : "default",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  touchAction: "pan-y",
                }}
              >
                {activeImage ? (
                  <img
                    src={activeImage.url}
                    alt={serviceName}
                    style={{
                      width: "100%",
                      // No re-crop: look photos are already 4:5 from the upload
                      // crop wizard; render at natural aspect (no cover-crop).
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{ color: "#94a3b8", padding: "40px", fontSize: "14px" }}>
                    No image
                  </div>
                )}

                {!isMobile && activeImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal((prev) => ({
                          ...prev,
                          imageIndex:
                            (prev.imageIndex - 1 + activeImages.length) % activeImages.length,
                        }));
                      }}
                      aria-label="Previous image"
                      style={{
                        position: "absolute",
                        left: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "32px",
                        height: "32px",
                        borderRadius: "999px",
                        border: "none",
                        background: "rgba(15,23,42,0.45)",
                        color: "#fff",
                        fontSize: "13px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.75,
                      }}
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal((prev) => ({
                          ...prev,
                          imageIndex: (prev.imageIndex + 1) % activeImages.length,
                        }));
                      }}
                      aria-label="Next image"
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "32px",
                        height: "32px",
                        borderRadius: "999px",
                        border: "none",
                        background: "rgba(15,23,42,0.45)",
                        color: "#fff",
                        fontSize: "13px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.75,
                      }}
                    >
                      <FaChevronRight />
                    </button>
                    <span
                      style={{
                        position: "absolute",
                        bottom: "14px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "rgba(15,23,42,0.65)",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {modal.imageIndex + 1} / {activeImages.length}
                    </span>
                  </>
                )}
              </div>

              {/* Details side */}
              <div
                style={{
                  padding: isMobile ? "18px 18px 22px" : "26px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                {/* Engagement row (likes / views / comments) */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: isMobile ? "16px" : "20px",
                    flexWrap: "wrap",
                    padding: "2px 0",
                  }}
                >
                  {activeLook?._id ? (
                    <LikesButton
                      lookId={activeLook._id}
                      apiBase={apiBase}
                      initialLikes={activeLook?.likeCount || 0}
                    />
                  ) : null}
                  {activeLook?._id ? (
                    <SaveButton lookId={activeLook._id} apiBase={apiBase} />
                  ) : null}
                  {activeLook?._id ? (
                    <RepostButton lookId={activeLook._id} apiBase={apiBase} />
                  ) : null}
                  <span style={{ color: "#64748b", display: "inline-flex", alignItems: "center", gap: "5px", fontSize: isMobile ? "12px" : "13px" }}>
                    <FaEye size={13} /> {(activeLook?.viewCount || 0).toLocaleString()} views
                  </span>
                  {isMobile ? (
                    // Always show the comment entry point on mobile so the
                    // bottom sheet can be opened even when there are 0 comments.
                    <button
                      onClick={() => commentsRef.current?.openSheet()}
                      aria-label="Open comments"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        border: "none",
                        background: "transparent",
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: "0",
                        touchAction: "manipulation",
                        WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <FaRegComment size={13} /> {(activeLook?.commentCount || 0).toLocaleString()}
                    </button>
                  ) : (activeLook?.commentCount || 0) > 0 ? (
                    <span style={{ color: "#64748b", display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
                      <FaRegComment size={13} /> {(activeLook.commentCount || 0).toLocaleString()}
                    </span>
                  ) : null}
                </div>

                {/* Title */}
                <h2
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "16px" : "22px",
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.3,
                    textAlign: "center",
                  }}
                >
                  {serviceName}
                </h2>

                {/* Professional (linked to the pro's public page when available) */}
                {(() => {
                  const proInner = (
                    <>
                      {proImage ? (
                        <img
                          src={proImage}
                          alt={proName}
                          style={{
                            width: isMobile ? "38px" : "44px",
                            height: isMobile ? "38px" : "44px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: isMobile ? "38px" : "44px",
                            height: isMobile ? "38px" : "44px",
                            borderRadius: "50%",
                            background: "#1d4f80",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: isMobile ? "14px" : "16px",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {initials}
                        </div>
                      )}
                      <div style={{ minWidth: 0, textAlign: "left" }}>
                        <div style={{ fontSize: isMobile ? "14px" : "15px", fontWeight: 600, color: "#0f172a" }}>
                          {proName}
                        </div>
                        <div style={{ fontSize: isMobile ? "12px" : "13px", color: "#6b7280" }}>
                          {bizName || roleLabel}
                        </div>
                      </div>
                    </>
                  );
                  // The canonical path is computed by the API (`staffProfilePath`): R3 professionals live under
                  // their business and location, R4/R7 freelancers under /freelancer. A flat /professional/<slug>
                  // link loses the business context and, for an independent professional, points at the wrong page.
                  const proUrl =
                    activeLook?.staffProfilePath ||
                    (activeLook?.staffSlug ? `/professional/${activeLook.staffSlug}` : null);
                  return proUrl ? (
                    <Link
                      to={proUrl}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        justifyContent: "center",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      {proInner}
                    </Link>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "center" }}>
                      {proInner}
                    </div>
                  );
                })()}

                {/* Duration + price (centered, with icons) */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  {duration ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontWeight: 600, color: "#2563eb", fontSize: isMobile ? "13px" : "14px" }}>
                      <FaClock size={13} /> {duration} min
                    </span>
                  ) : null}
                  {priceLabel ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontWeight: 700, color: "#059669", fontSize: isMobile ? "13px" : "14px" }}>
                      <FaTags size={13} /> {priceLabel}
                    </span>
                  ) : null}
                </div>

                {modal.error && (
                  <div style={{ fontSize: "12px", color: "#b91c1c" }}>{modal.error}</div>
                )}

                {/* CTA */}
                <button
                  onClick={handleBook}
                  disabled={!activeLook}
                  style={{
                    width: "100%",
                    padding: isMobile ? "12px 18px" : "13px 20px",
                    background: "var(--color-primary, #2d6da3)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: isMobile ? "14px" : "15px",
                    fontWeight: 700,
                    cursor: activeLook ? "pointer" : "default",
                    opacity: activeLook ? 1 : 0.6,
                    boxShadow: "0 8px 20px rgba(45,109,163,0.3)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Book This Look
                </button>

                {/* Share */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <ShareButtons
                    url={shareUrl}
                    imageUrl={resolveLookShareImageUrl(activeLook?._id, activeLook?.cardVersion)}
                    title={serviceName}
                    text={`Book ${serviceName} at ${bizName || "OGESTYLE"}${bizName ? " on OGESTYLE" : ""}`}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <a
                    onClick={() => {
                      const id = activeLook?._id || activeLook?.recordId;
                      if (id) nav(`/look/${id}`);
                    }}
                    style={{ color: "#2563eb", cursor: "pointer", fontSize: isMobile ? "12px" : "13px", fontWeight: 600 }}
                  >
                    View full page →
                  </a>
                  {modal.loading && (
                    <span style={{ fontSize: "12px", color: "#9ca3af" }}>Loading details…</span>
                  )}
                </div>

                {/* Comments — desktop inline; mobile opens the bottom sheet via the comment icon */}
                {activeLook?._id ? (
                  <CommentsSection
                    ref={commentsRef}
                    lookId={activeLook._id}
                    apiBase={apiBase}
                    isMobile={isMobile}
                  />
                ) : null}

                {/* More looks */}
                {activeLook?.moreLooks && activeLook.moreLooks.length > 0 && (
                  <div style={{ borderTop: "1px solid #edf1f6", paddingTop: "14px" }}>
                    <p style={{ margin: "0 0 10px 0", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#0f172a" }}>
                      More looks by {proName}
                    </p>
                    <div style={{ display: "flex", gap: "10px", overflowX: "auto", paddingBottom: "4px" }}>
                      {activeLook.moreLooks.map((l) => (
                        <div
                          key={l._id}
                          onClick={() => openLookInLightbox(l._id)}
                          style={{ cursor: "pointer", flexShrink: 0, width: "108px" }}
                        >
                          <img
                            src={l.imageUrl}
                            alt={l.serviceName}
                            style={{
                              width: "100%",
                              height: "108px",
                              objectFit: "cover",
                              borderRadius: "10px",
                              display: "block",
                              border: "1px solid #e5e7eb",
                            }}
                          />
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#374151",
                              marginTop: "4px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {l.serviceName}
                          </div>
                          <div style={{ fontSize: "10px", color: "#9ca3af" }}>
                            {(l.viewCount || 0).toLocaleString()} views
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LooksGallery;
